import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth';

const TimezoneContext = createContext();

export const TimezoneProvider = ({ children }) => {
  const [currentTimezone, setCurrentTimezone] = useState('UTC');
  const [loading, setLoading] = useState(false);

  // Initialize timezone based on user preference or browser detection
  useEffect(() => {
    const initializeTimezone = async () => {
      const user = authService.getCurrentUser();
      
      if (user && user.timezone) {
        // User is authenticated and has a timezone preference
        setCurrentTimezone(user.timezone);
      } else {
        // Fall back to browser timezone detection or localStorage
        const savedTimezone = localStorage.getItem('timezone') || 
                              Intl.DateTimeFormat().resolvedOptions().timeZone || 
                              'UTC';
        setCurrentTimezone(savedTimezone);
      }
    };

    initializeTimezone();
  }, []);

  // Update timezone preference for authenticated user
  const updateTimezone = async (timezone) => {
    setLoading(true);
    
    try {
      const user = authService.getCurrentUser();
      
      if (user) {
        // User is authenticated - update in database via profile update
        await authService.updateProfile({
          username: user.username,
          email: user.email,
          language: user.language,
          timezone: timezone
        });
        console.log('Timezone updated in database:', timezone);
      } else {
        // User not authenticated - just update localStorage
        localStorage.setItem('timezone', timezone);
      }
      
      // Update local state
      setCurrentTimezone(timezone);
      
    } catch (error) {
      console.error('Failed to update timezone:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sync timezone when user logs in
  const syncUserTimezone = (user) => {
    if (user && user.timezone && user.timezone !== currentTimezone) {
      setCurrentTimezone(user.timezone);
      console.log('Timezone synced from user data:', user.timezone);
    }
  };

  // Reset to browser/localStorage timezone when user logs out
  const resetToLocalTimezone = () => {
    const savedTimezone = localStorage.getItem('timezone') || 
                          Intl.DateTimeFormat().resolvedOptions().timeZone || 
                          'UTC';
    setCurrentTimezone(savedTimezone);
    console.log('Timezone reset to local:', savedTimezone);
  };

  // Check and sync user timezone from server (for when admin changes current user's timezone)
  const checkAndSyncUserTimezone = async () => {
    try {
      const user = authService.getCurrentUser();
      if (!user) return;

      // Fetch the latest user data from server
      const updatedUser = await authService.fetchCurrentUser();
      
      // Check if timezone has changed
      if (updatedUser.timezone && updatedUser.timezone !== currentTimezone) {
        // Update local state immediately
        setCurrentTimezone(updatedUser.timezone);
        console.log(`Timezone automatically synced to: ${updatedUser.timezone}`);
      }
    } catch (error) {
      console.error('Failed to check and sync user timezone:', error);
    }
  };

  // Format date with current timezone
  const formatDateWithTimezone = (dateString, options = {}) => {
    if (!dateString) return '-';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: currentTimezone
    };
    
    return new Date(dateString).toLocaleDateString('en-US', {
      ...defaultOptions,
      ...options
    });
  };

  // Format date and time with current timezone
  const formatDateTimeWithTimezone = (dateString, options = {}) => {
    if (!dateString) return '-';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: currentTimezone
    };
    
    return new Date(dateString).toLocaleString('en-US', {
      ...defaultOptions,
      ...options
    });
  };

  const value = {
    currentTimezone,
    updateTimezone,
    loading,
    syncUserTimezone,
    resetToLocalTimezone,
    checkAndSyncUserTimezone,
    formatDateWithTimezone,
    formatDateTimeWithTimezone
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export default TimezoneContext;